import React from 'react';

import { Helmet } from 'react-helmet-async';

import ErrorPage from '@/components/errors/ErrorPage';
import SingleCardTemplate from '@/components/layouts/SingleCardTemplate';
import Loading from '@/components/ui/loading/Loading';
import { pageTitles } from '@/constants/pageTitles';
import useValidateInvitation from '@/hooks/invitations/useValidateInvitation';

import { PARENTS_LIMIT_EXCEEDED_ERROR_CODE } from '../constants';
import { getInvitationToken } from '../invitationToken';

import InvitationRegisterInputForm, {
  type InvitationRegisterFormValues,
} from './InvitationRegisterInputForm';
import InvitationValidateErrorPage from './InvitationValidateErrorPage';

interface Props {
  defaultFormValues: InvitationRegisterFormValues | null;
  onSubmit: (values: InvitationRegisterFormValues) => void;
}

const InvitationRegisterInputContent: React.FC<
  Props & { invitationToken: string }
> = ({ invitationToken, defaultFormValues, onSubmit }) => {
  const { data, isFetching, error } = useValidateInvitation({
    invitationToken,
  });
  const student = data?.student;
  const invitedParentEmail = data?.invited_parent_email;

  if (isFetching) {
    return (
      <SingleCardTemplate title={pageTitles.invitationRegisterInput}>
        <Loading />
      </SingleCardTemplate>
    );
  }

  if (error) {
    if (error.error_code === PARENTS_LIMIT_EXCEEDED_ERROR_CODE) {
      return (
        <InvitationValidateErrorPage
          type="parents_limit"
          pageTitle={pageTitles.invitationRegisterInput}
        />
      );
    }
    if (error.status === 403) {
      return (
        <InvitationValidateErrorPage
          type="expired"
          pageTitle={pageTitles.invitationRegisterInput}
        />
      );
    }
    if (error.status === 400 || error.status === 422) {
      return (
        <InvitationValidateErrorPage
          type="invalid"
          pageTitle={pageTitles.invitationRegisterInput}
        />
      );
    }
    if (error.status >= 500) {
      return <ErrorPage type="internal_server_error" />;
    }
    return <ErrorPage />;
  }

  if (!student || !invitedParentEmail) {
    return <ErrorPage type="not_found" />;
  }

  return (
    <SingleCardTemplate title={pageTitles.invitationRegisterInput}>
      <Helmet>
        <title>{pageTitles.invitationRegisterInput}</title>
      </Helmet>
      <InvitationRegisterInputForm
        email={invitedParentEmail}
        code={student.code}
        defaultFormValues={defaultFormValues ?? undefined}
        onSubmit={onSubmit}
      />
    </SingleCardTemplate>
  );
};

const InvitationRegisterInputPage: React.FC<Props> = (props) => {
  const invitationToken = getInvitationToken();

  if (!invitationToken) {
    return (
      <InvitationValidateErrorPage
        type="token_missing"
        pageTitle={pageTitles.invitationRegisterInput}
      />
    );
  }

  return (
    <InvitationRegisterInputContent
      invitationToken={invitationToken}
      {...props}
    />
  );
};

export default InvitationRegisterInputPage;
